import { Link } from '@inertiajs/react';
import type { StateResponse } from '@/types/market';
import {
  formatBananas,
  formatPercent,
  formatPrice,
  formatSigned,
} from './format';
import { useLivePortfolio, useMarketState } from './live';
import { RollingNumber } from './rolling-number';

export function PortfolioClient({
  hackSlug,
  initialState,
}: {
  hackSlug: string;
  initialState: StateResponse;
}) {
  const state = useMarketState(initialState);
  const { portfolio: data, error } = useLivePortfolio(state, true);

  if (error)
    return (
      <p className="font-mono text-destructive">
        No se pudo cargar el portfolio.
      </p>
    );
  if (!data)
    return (
      <p className="font-mono text-primary/70 uppercase tracking-wider">
        Cargando…
      </p>
    );
  if ('error' in data)
    return <p className="font-mono text-destructive">{data.error}</p>;

  const pnlPositive = data.totalPnlBananas >= 0;

  return (
    <div className="flex flex-col gap-6">
      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="mb-4 border-primary/20 border-b pb-3">
          <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
            Tu cuenta
          </p>
          <h1 className="font-bold text-3xl text-primary sm:text-4xl">
            Portafolio
          </h1>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat
            label="Cash"
            value={
              <RollingNumber value={data.cashBananas} format={formatBananas} />
            }
          />
          <Stat
            label="Posiciones"
            value={
              <RollingNumber
                value={data.totalPositionsValue}
                format={formatBananas}
              />
            }
          />
          <Stat
            label="Total"
            value={
              <RollingNumber
                value={data.totalValueBananas}
                format={formatBananas}
              />
            }
          />
          <Stat
            label="P&L"
            value={
              <>
                <RollingNumber
                  value={data.totalPnlBananas}
                  format={(n) => formatSigned(n)}
                />{' '}
                (
                <RollingNumber
                  value={data.totalPnlPct}
                  format={(n) => `${n.toFixed(1)}%`}
                />
                )
              </>
            }
            valueClass={pnlPositive ? 'text-green-600' : 'text-red-600'}
          />
        </div>
      </section>

      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <h2 className="mb-4 border-primary/20 border-b pb-3 font-bold text-primary text-xl">
          Posiciones
        </h2>
        {data.positions.length === 0 ? (
          <p className="font-mono text-primary/60 text-sm uppercase tracking-wider">
            Aún no tienes acciones.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {data.positions.map((p) => {
              const positive = p.pnlBananas >= 0;
              return (
                <Link
                  key={p.projectId}
                  href={`/${hackSlug}/market/${p.projectSlug}`}
                  className="grid grid-cols-2 items-center gap-3 border-primary border-l-4 py-3 pr-3 pl-4 transition-colors hover:bg-primary/5 sm:grid-cols-6"
                >
                  <div className="col-span-2 sm:col-span-2">
                    <p className="font-bold text-primary">{p.projectName}</p>
                    <p className="font-mono text-[10px] text-primary/60 uppercase tracking-wider">
                      <RollingNumber
                        value={p.currentPrice}
                        format={formatPercent}
                      />{' '}
                      chance · avg {formatPrice(p.avgCostBananas)}
                    </p>
                  </div>
                  <Cell label="Acciones">{p.shares}</Cell>
                  <Cell label="Valor">
                    <RollingNumber
                      value={p.valueBananas}
                      format={formatBananas}
                    />
                  </Cell>
                  <Cell
                    label="P&L"
                    valueClass={positive ? 'text-green-600' : 'text-red-600'}
                  >
                    <RollingNumber
                      value={p.pnlBananas}
                      format={(n) => formatSigned(n, ' 🍌')}
                    />
                  </Cell>
                  <Cell
                    label="%"
                    valueClass={positive ? 'text-green-600' : 'text-red-600'}
                  >
                    <RollingNumber
                      value={p.pnlPct}
                      format={(n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`}
                    />
                  </Cell>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <h2 className="mb-4 border-primary/20 border-b pb-3 font-bold text-primary text-xl">
          Trades recientes
        </h2>
        {data.recentTrades.length === 0 ? (
          <p className="font-mono text-primary/60 text-sm uppercase tracking-wider">
            Aún no hay trades.
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {data.recentTrades.map((t) => {
              const isBuy = t.side === 'buy';
              return (
                <div
                  key={t.id}
                  className={`grid grid-cols-2 items-center gap-3 border-l-4 py-2 pr-3 pl-4 sm:grid-cols-6 ${
                    isBuy ? 'border-green-600' : 'border-red-600'
                  }`}
                >
                  <div className="col-span-2 sm:col-span-2">
                    <Link
                      href={`/${hackSlug}/market/${t.projectSlug}`}
                      className="font-bold text-primary hover:underline"
                    >
                      {t.projectName}
                    </Link>
                    <p className="font-mono text-[10px] text-primary/60 uppercase tracking-wider">
                      {new Date(t.createdAt).toLocaleTimeString()} ·{' '}
                      <span
                        className={isBuy ? 'text-green-600' : 'text-red-600'}
                      >
                        {isBuy ? 'BUY' : 'SELL'}
                      </span>
                    </p>
                  </div>
                  <Cell label="Acciones">{t.shares}</Cell>
                  <Cell label="Bananas">{t.grossBananas} 🍌</Cell>
                  <Cell label="Fee">{t.feeBananas} 🍌</Cell>
                  <Cell label="Precio">
                    {formatPrice(Number(t.priceAtTrade))}
                  </Cell>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="border-primary border-l-4 pl-4">
      <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
        {label}
      </p>
      <p
        className={`font-bold font-mono text-primary text-xl sm:text-2xl ${
          valueClass ?? ''
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Cell({
  label,
  children,
  valueClass,
}: {
  label: string;
  children: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div>
      <p className="font-mono text-[10px] text-primary/60 uppercase tracking-wider">
        {label}
      </p>
      <p
        className={`font-bold font-mono text-primary text-sm tabular-nums ${
          valueClass ?? ''
        }`}
      >
        {children}
      </p>
    </div>
  );
}
